import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Nav, Row, Table } from "react-bootstrap";
import { getTranslatableLanguages, languages, LanguageCode } from "../../models/company";
import { useAppSelector } from "../../store";
import TinyEditor from "../../utils/TinyEditor";
import { queryClient } from "../../utils/http/queryClient";
import { unwrapSingleParagraph } from "../../utils/text";
import {
  checkTranslationKeyCompleteness,
  deleteTranslationKey,
  fetchTranslationKeyValues,
  publishTranslationKeys,
  saveTranslationKeyDrafts,
  TranslationKeyStatus,
} from "../../utils/http/translations";

// Shows/edits the `t("key")` translation keys used by a V3 mail template, one key at a time,
// for every language the company has marked translatable. Values live in Compass, one record
// per company·key·language·status (draft/approved).
//
// `status`/`onStatusChange` are controlled by the parent (Template.tsx) so TemplatePreview can
// render with the same draft/approved toggle this panel edits.
function TranslationsPanel({
  keys,
  status,
  onStatusChange,
}: {
  keys: string[];
  status: TranslationKeyStatus;
  onStatusChange: (status: TranslationKeyStatus) => void;
}) {
  const activeCompany = useAppSelector((state) => state.company.activeCompany);
  const companyId = activeCompany?.id || activeCompany?.name;

  const translatableLanguageIds = getTranslatableLanguages(activeCompany?.info?.languages);
  const translatableLanguages = translatableLanguageIds.length
    ? languages.filter((l) => translatableLanguageIds.includes(l.id as LanguageCode))
    : languages;

  // `keys` is a new array on every render; key the query on its stringified contents instead
  // to avoid re-fetching on unrelated re-renders.
  const keysSignature = [...keys].sort().join(" ");
  const stableKeys = useMemo(() => keys, [keysSignature]);

  const [selectedKey, setSelectedKey] = useState<string>(keys[0] || "");
  // Values not yet flushed to the backend, keyed by `${key}:${language}`.
  const [pendingValues, setPendingValues] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  // Key whose publish just succeeded, so the confirmation shows on that row only.
  const [justPublishedKey, setJustPublishedKey] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Two-step arm/confirm for the delete button, keyed per `${key}:${language}`.
  const [armedDeleteKey, setArmedDeleteKey] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [publishingKey, setPublishingKey] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedKey || !keys.includes(selectedKey)) {
      setSelectedKey(keys[0] || "");
    }
  }, [keys, selectedKey]);

  const valuesQuery = useQuery({
    queryKey: ["translationKeyValues", companyId, stableKeys, status],
    queryFn: async () => {
      const entries = await Promise.all(
        translatableLanguages.map(
          async (l) =>
            [
              l.id,
              await fetchTranslationKeyValues({ companyId: companyId!, keys: stableKeys, language: l.id, status }),
            ] as const,
        ),
      );
      return Object.fromEntries(entries) as Record<string, Record<string, string>>;
    },
    enabled: !!companyId && stableKeys.length > 0,
  });

  // Reset locally-pending edits when switching key/status, not on every query refetch (an
  // autosave-triggered background refetch shouldn't clobber an in-progress edit).
  useEffect(() => {
    setPendingValues({});
  }, [status, stableKeys]);

  const valueFor = (key: string, languageId: string): string => {
    const pendingKey = `${key}:${languageId}`;
    if (pendingKey in pendingValues) return pendingValues[pendingKey];
    return valuesQuery.data?.[languageId]?.[key] ?? "";
  };

  // Approved values, needed while editing drafts so a key with no draft yet can still show
  // what's currently live. Skipped when `status` is already "approved" -- `valuesQuery` has them.
  const approvedValuesQuery = useQuery({
    queryKey: ["translationKeyValues", companyId, stableKeys, "approved"],
    queryFn: async () => {
      const entries = await Promise.all(
        translatableLanguages.map(
          async (l) =>
            [
              l.id,
              await fetchTranslationKeyValues({
                companyId: companyId!,
                keys: stableKeys,
                language: l.id,
                status: "approved",
              }),
            ] as const,
        ),
      );
      return Object.fromEntries(entries) as Record<string, Record<string, string>>;
    },
    enabled: !!companyId && stableKeys.length > 0 && status === "draft",
  });

  const approvedValueFor = (key: string, languageId: string): string => {
    const source = status === "approved" ? valuesQuery.data : approvedValuesQuery.data;
    return source?.[languageId]?.[key] ?? "";
  };

  // Whether a draft record exists for (key, language), as opposed to being empty. `:batchGet`
  // omits keys with no record entirely, so presence in the response is the real signal.
  const hasDraft = (key: string, languageId: string): boolean => {
    const pendingKey = `${key}:${languageId}`;
    if (pendingKey in pendingValues) return true;
    return !!valuesQuery.data?.[languageId] && key in valuesQuery.data[languageId];
  };

  // Debounced autosave, per (key, language).
  const saveTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const flushSaveNow = async (key: string, languageId: string, value: string) => {
    try {
      await saveTranslationKeyDrafts({
        companyId: companyId!,
        translations: [{ key, language: languageId, value: unwrapSingleParagraph(value) ?? value }],
      });
      queryClient.invalidateQueries({ queryKey: ["translationKeyValues", companyId] });
      queryClient.invalidateQueries({ queryKey: ["translationKeyCompleteness", companyId] });
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", "render"] });
    } catch (e: any) {
      setSaveError(`Could not save translation draft: ${e?.info ?? e?.message ?? e}`);
    }
  };

  const scheduleSave = (key: string, languageId: string, value: string) => {
    const pendingKey = `${key}:${languageId}`;
    setPendingValues((prev) => ({ ...prev, [pendingKey]: value }));
    setSaveError(null);

    if (saveTimeoutsRef.current[pendingKey]) clearTimeout(saveTimeoutsRef.current[pendingKey]);
    saveTimeoutsRef.current[pendingKey] = setTimeout(() => {
      delete saveTimeoutsRef.current[pendingKey];
      flushSaveNow(key, languageId, value);
    }, 1000);
  };

  // Cancels a pending debounced autosave for (key, language) and performs it immediately
  // instead -- otherwise the stale timeout could still fire later (e.g. right after a publish)
  // and re-upsert a draft with out-of-date content. No-op if nothing is pending.
  const flushPendingSave = async (key: string, languageId: string) => {
    const pendingKey = `${key}:${languageId}`;
    const timeoutId = saveTimeoutsRef.current[pendingKey];
    if (!timeoutId) return;
    clearTimeout(timeoutId);
    delete saveTimeoutsRef.current[pendingKey];
    await flushSaveNow(key, languageId, pendingValues[pendingKey]);
  };

  // Deletes the (key, language) draft record, rather than saving "".
  const deleteTranslation = async (key: string, languageId: string) => {
    const pendingKey = `${key}:${languageId}`;
    setDeleteError(null);
    setDeletingKey(pendingKey);

    if (saveTimeoutsRef.current[pendingKey]) {
      clearTimeout(saveTimeoutsRef.current[pendingKey]);
      delete saveTimeoutsRef.current[pendingKey];
    }

    try {
      await deleteTranslationKey({ companyId: companyId!, key, language: languageId, status: "draft" });

      setPendingValues((prev) => {
        const next = { ...prev };
        delete next[pendingKey];
        return next;
      });
      setArmedDeleteKey(null);
      queryClient.invalidateQueries({ queryKey: ["translationKeyValues", companyId] });
      queryClient.invalidateQueries({ queryKey: ["translationKeyCompleteness", companyId] });
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", "render"] });
    } catch (e: any) {
      setDeleteError(`Could not delete translation draft: ${e?.info ?? e?.message ?? e}`);
    } finally {
      setDeletingKey(null);
    }
  };

  const publishKey = async (key: string) => {
    if (!key || !companyId) return;
    setPublishError(null);
    setJustPublishedKey(null);
    setPublishingKey(key);
    try {
      // Include whatever's currently typed (but not yet autosaved), and make sure the
      // debounce can't fire afterward and resurrect the draft this is about to remove.
      await Promise.all(translatableLanguages.map((l) => flushPendingSave(key, l.id)));

      await publishTranslationKeys({
        companyId,
        pairs: translatableLanguages.map((l) => ({ key, language: l.id })),
      });
      setJustPublishedKey(key);
      setPendingValues((prev) => {
        const next = { ...prev };
        for (const l of translatableLanguages) {
          delete next[`${key}:${l.id}`];
        }
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ["translationKeyValues", companyId] });
      queryClient.invalidateQueries({ queryKey: ["translationKeyCompleteness", companyId] });
      queryClient.invalidateQueries({ queryKey: ["mailTemplates", "render"] });
    } catch (e: any) {
      setPublishError(`Could not publish: ${e?.info ?? e?.message ?? e}`);
    } finally {
      setPublishingKey(null);
    }
  };

  // "Fully translated" indicator, per language, for the currently active status.
  const completenessQuery = useQuery({
    queryKey: [
      "translationKeyCompleteness",
      companyId,
      stableKeys,
      status,
      translatableLanguages.map((l) => l.id).join(","),
    ],
    queryFn: async () => {
      const entries = await Promise.all(
        translatableLanguages.map(
          async (l) =>
            [
              l.id,
              await checkTranslationKeyCompleteness({ companyId: companyId!, keys: stableKeys, language: l.id, status }),
            ] as const,
        ),
      );
      return Object.fromEntries(entries) as Record<
        string,
        { missing: string[]; total: number; translated: number }
      >;
    },
    enabled: !!companyId && keys.length > 0,
  });

  const completenessByLanguage = completenessQuery.data ?? {};
  const canPublishKey = (key: string) => translatableLanguages.some((l) => hasDraft(key, l.id));

  // Per-key draft/approved markers for the key dropdown - independent of the active `status`,
  // so both are known regardless of which one is currently being edited/viewed. Deduped against
  // `completenessQuery` above by react-query when its key happens to match one of these.
  const draftCompletenessQuery = useQuery({
    queryKey: [
      "translationKeyCompleteness",
      companyId,
      stableKeys,
      "draft",
      translatableLanguages.map((l) => l.id).join(","),
    ],
    queryFn: async () => {
      const entries = await Promise.all(
        translatableLanguages.map(
          async (l) =>
            [
              l.id,
              await checkTranslationKeyCompleteness({
                companyId: companyId!,
                keys: stableKeys,
                language: l.id,
                status: "draft",
              }),
            ] as const,
        ),
      );
      return Object.fromEntries(entries) as Record<string, { missing: string[]; total: number; translated: number }>;
    },
    enabled: !!companyId && keys.length > 0,
  });

  const approvedCompletenessQuery = useQuery({
    queryKey: [
      "translationKeyCompleteness",
      companyId,
      stableKeys,
      "approved",
      translatableLanguages.map((l) => l.id).join(","),
    ],
    queryFn: async () => {
      const entries = await Promise.all(
        translatableLanguages.map(
          async (l) =>
            [
              l.id,
              await checkTranslationKeyCompleteness({
                companyId: companyId!,
                keys: stableKeys,
                language: l.id,
                status: "approved",
              }),
            ] as const,
        ),
      );
      return Object.fromEntries(entries) as Record<string, { missing: string[]; total: number; translated: number }>;
    },
    enabled: !!companyId && keys.length > 0,
  });

  // Whether `key` has a value in at least one translatable language for the given completeness
  // data. Undefined while that data is still loading, so callers can render nothing rather than
  // a misleading "absent" marker.
  const keyHasValueIn = (
    key: string,
    data: Record<string, { missing: string[] }> | undefined,
  ): boolean | undefined => {
    if (!data) return undefined;
    return translatableLanguages.some((l) => !data[l.id]?.missing.includes(key));
  };

  // Keys are stored fully qualified (`EVENT.ACTION.TARGET.name`); the prefix is the same for
  // every key of a given target, so only the last segment is worth showing. The full key stays
  // available as the row's tooltip.
  const shortKeyOf = (key: string): string => key.split(".").pop() || key;

  // Presence cell for the key table: a value exists / doesn't / isn't known yet. `undefined`
  // means the completeness query is still loading, where a red cross would be a lie.
  const renderPresence = (present: boolean | undefined, kind: "draft" | "approved") => {
    if (present === undefined) return <span className="text-muted">…</span>;
    if (present) {
      return (
        <span className="text-success" title={kind === "draft" ? "Has a draft value" : "Has an approved value"}>
          ✓
        </span>
      );
    }
    return (
      <span
        className="text-muted opacity-50"
        title={kind === "draft" ? "No draft value yet" : "Not published yet"}
      >
        –
      </span>
    );
  };

  if (keys.length === 0) return null;

  return (
    <Card>
      <Card.Header className="d-flex align-items-end justify-content-between bg-white pb-0">
        <Nav variant="tabs" className="card-header-tabs" style={{ marginBottom: 0 }}>
          <Nav.Item>
            <Nav.Link
              active={status === "draft"}
              onClick={() => {
                onStatusChange("draft");
                setJustPublishedKey(null);
              }}
            >
              Draft
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              active={status === "approved"}
              onClick={() => {
                onStatusChange("approved");
                setJustPublishedKey(null);
              }}
            >
              Approved
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <div className="d-flex align-items-center gap-2 pb-2">
          {status === "approved" && <span className="text-muted small">Read-only — switch to Draft to edit</span>}
          {translatableLanguages.map((l) => {
            const completeness = completenessByLanguage[l.id];
            const isComplete = completeness && completeness.missing.length === 0;
            return (
              <Badge bg={isComplete ? "success" : "secondary"} key={l.id}>
                {l.name}: {completeness ? `${completeness.translated}/${completeness.total}` : "…"}
              </Badge>
            );
          })}
        </div>
      </Card.Header>

      <Card.Body>
        <div className="border rounded mb-4" style={{ maxHeight: 260, overflowY: "auto" }}>
          <Table hover size="sm" className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: 40 }} className="text-center" title="Currently selected key">
                  <span className="visually-hidden">Selected</span>
                </th>
                <th style={{ width: 90 }} className="text-center">
                  Approved
                </th>
                <th style={{ width: 90 }} className="text-center">
                  Draft
                </th>
                <th>Key</th>
                {status === "draft" && <th style={{ width: 110 }} className="text-end" />}
              </tr>
            </thead>
            <tbody>
              {[...keys].sort().map((key) => {
                const hasDraftValue = keyHasValueIn(key, draftCompletenessQuery.data);
                const hasApprovedValue = keyHasValueIn(key, approvedCompletenessQuery.data);
                const isSelected = key === selectedKey;
                return (
                  <tr
                    key={key}
                    onClick={() => setSelectedKey(key)}
                    className={isSelected ? "table-primary" : undefined}
                    style={{ cursor: "pointer" }}
                  >
                    <td className="text-center">
                      {isSelected ? (
                        <span className="text-primary" aria-label="Currently selected">
                          ●
                        </span>
                      ) : (
                        <span className="text-muted opacity-25">○</span>
                      )}
                    </td>
                    <td className="text-center">{renderPresence(hasApprovedValue, "approved")}</td>
                    <td className="text-center">{renderPresence(hasDraftValue, "draft")}</td>
                    <td className={isSelected ? "fw-semibold text-break" : "text-break"} title={key}>
                      {shortKeyOf(key)}
                    </td>
                    {status === "draft" && (
                      <td className="text-end">
                        {justPublishedKey === key ? (
                          <Badge bg="success">Published</Badge>
                        ) : (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            title="Publish this key's drafts for all languages"
                            disabled={!canPublishKey(key) || publishingKey === key}
                            // Don't let publishing also move the selection away from the key
                            // currently open in the editors below.
                            onClick={(e) => {
                              e.stopPropagation();
                              publishKey(key);
                            }}
                          >
                            {publishingKey === key ? "Publishing…" : "Publish"}
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>


        {valuesQuery.isError && (
          <Alert variant="danger">
            Could not load translation values:{" "}
            {(valuesQuery.error as any)?.info ?? (valuesQuery.error as any)?.message ?? String(valuesQuery.error)}
          </Alert>
        )}
        {saveError && <Alert variant="danger">{saveError}</Alert>}
        {publishError && <Alert variant="danger">{publishError}</Alert>}
        {deleteError && <Alert variant="danger">{deleteError}</Alert>}

        {selectedKey &&
          translatableLanguages.map((l) => {
          const pendingKey = `${selectedKey}:${l.id}`;
          const draftExists = hasDraft(selectedKey, l.id);
          // No draft yet: show the approved text read-only, so it's clear what's live and what
          // "Maak draft aan" will start from.
          const showingApproved = status === "draft" && !draftExists;
          const approvedValue = approvedValueFor(selectedKey, l.id);
          return (
            // Keyed on `l.id` only, not `selectedKey`/`status`, to avoid remounting TinyEditor
            // on every switch.
            <Container key={l.id} className="mb-3">
              <Row className="align-items-center mb-1">
                <Col>
                  <div className="text-muted" style={{ fontSize: "12px" }}>
                    {l.name}
                    {showingApproved && (
                      <span className="ms-2 fst-italic">
                        {approvedValue ? "approved text, read-only — no draft yet" : "no draft and nothing approved yet"}
                      </span>
                    )}
                  </div>
                </Col>
                {status === "draft" && (
                  <Col xs="auto" className="d-flex align-items-center">
                    {!draftExists ? (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => scheduleSave(selectedKey, l.id, approvedValue)}
                      >
                        <i className="bi bi-plus-lg me-1" />
                        Maak draft aan
                      </Button>
                    ) : armedDeleteKey === pendingKey ? (
                      <>
                        <Button
                          variant="danger"
                          size="sm"
                          className="me-1"
                          disabled={deletingKey === pendingKey}
                          onClick={() => deleteTranslation(selectedKey, l.id)}
                        >
                          Yes, delete
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          disabled={deletingKey === pendingKey}
                          onClick={() => setArmedDeleteKey(null)}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        title="Verwijder deze vertaling"
                        onClick={() => setArmedDeleteKey(pendingKey)}
                      >
                        <i className="bi bi-trash" />
                      </Button>
                    )}
                  </Col>
                )}
              </Row>
              <Row>
                <Col>
                  <TinyEditor
                    value={showingApproved ? approvedValue : valueFor(selectedKey, l.id)}
                    placeholder=""
                    disabled={status !== "draft" || !draftExists}
                    onChange={(txt) => scheduleSave(selectedKey, l.id, txt)}
                  />
                </Col>
              </Row>
            </Container>
          );
        })}
      </Card.Body>
    </Card>
  );
}

export default TranslationsPanel;
